'use client'

import { cx } from 'classix'
import { useLayoutLayers } from 'hooks/useLayoutLayers/useLayoutLayers'
import {
	type Dispatch,
	forwardRef,
	type SetStateAction,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Keyboard } from 'types/keyboard'
import styles from './Dialog.module.css'

const DIALOG = 'dialog'
const ALERT = 'alert'

interface DialogPros {
	readonly title: string
	readonly containerClassName?: string
	readonly children: React.ReactNode
	readonly preventClose?: boolean
	readonly className?: string
	readonly keepContent?: boolean
	readonly opened: boolean
	readonly 'data-testid'?: string
	readonly id?: string
	readonly setOpened: Dispatch<SetStateAction<boolean>>
	readonly onClose?: () => void
	readonly onOpen?: () => void
	readonly nonModal?: boolean
	readonly location?: {
		top?: string
		left?: string
		right?: string
		bottom?: string
	}
	readonly targetLayer?: keyof ReturnType<typeof useLayoutLayers>
}

export interface DialogAPI {
	show: () => void
	close: () => void
	open: boolean
	element: HTMLDialogElement | null
}

function DialogComponent(
	{
		title,
		containerClassName = '',
		children,
		onClose,
		onOpen,
		opened,
		keepContent = false,
		preventClose = false,
		className = '',
		setOpened,
		location,
		nonModal = false,
		'data-testid': dataTestId,
		id,
		targetLayer = 'dialogs',
	}: DialogPros,
	ref: React.Ref<HTMLDialogElement>
) {
	const [onClient, setOnClient] = useState(false)
	const [keepChildrenRendered, setKeepChildrenRendered] = useState(false)
	const dialogRef = useRef<HTMLDialogElement | null>(null)
	const animationRef = useRef<HTMLDivElement | null>(null)
	const prevOpenedRef = useRef<boolean>(opened)

	const layoutLayers = useLayoutLayers()
	const { application } = layoutLayers
	const layer = layoutLayers[targetLayer]

	const close = useCallback(() => {
		if (onClient) {
			setOpened(false)
		}
	}, [setOpened, onClient])

	// Exposing Dialog API to parent.
	useImperativeHandle(ref, () => dialogRef?.current as HTMLDialogElement, [
		onClient,
	])

	const cancelHandle = useCallback(
		(e: Event) => {
			if (onClient) {
				e.preventDefault()
				e.stopPropagation()
				if (preventClose) {
					return
				}
				close()
			}
		},
		[close, preventClose, onClient]
	)

	const scapeKeyHandle = useCallback(
		(e: KeyboardEvent) => {
			if (e.code === Keyboard.Escape) {
				cancelHandle(e)
			}
		},
		[cancelHandle]
	)

	useEffect(() => {
		if (onClient && opened && prevOpenedRef.current !== opened) {
			prevOpenedRef.current = opened
			setKeepChildrenRendered(true)
			dialogRef.current?.show()
			if (!preventClose) {
				dialogRef.current?.addEventListener('keydown', scapeKeyHandle)
			}
			if (!nonModal) {
				application?.setAttribute('inert', '')
			}
			onOpen?.()
		}
		if (onClient && !opened && prevOpenedRef.current !== opened) {
			prevOpenedRef.current = opened
			onClose?.()
			if (!preventClose) {
				dialogRef.current?.removeEventListener('keydown', scapeKeyHandle)
			}
			application?.removeAttribute('inert')
			setKeepChildrenRendered(keepContent)
			dialogRef.current?.close()
		}
	}, [onClient, opened, preventClose, scapeKeyHandle, nonModal])

	const clickHandle = useCallback(
		(e: MouseEvent) => {
			if (onClient) {
				if (e.target !== dialogRef.current || preventClose) {
					return
				}
				e.preventDefault()
				application?.removeAttribute('inert')
				onClose?.()
				close()
			}
		},
		[close, preventClose, onClose, onClient]
	)

	useEffect(() => {
		if (onClient && opened) {
			dialogRef.current?.focus()
		}
	}, [opened, onClient])

	useEffect(() => {
		let reference: HTMLElement | null = null

		const keyDownHandle = (e: KeyboardEvent) => {
			if (e.key === Keyboard.Escape && preventClose) {
				e.preventDefault()
			}
		}

		if (onClient) {
			reference = dialogRef.current
			reference?.addEventListener('keydown', keyDownHandle)
			reference?.addEventListener('cancel', cancelHandle)
			reference?.addEventListener('click', clickHandle)
		}

		return () => {
			reference?.removeEventListener('cancel', cancelHandle)
			reference?.removeEventListener('click', clickHandle)
			reference?.removeEventListener('keydown', keyDownHandle)
		}
	}, [cancelHandle, clickHandle, preventClose, onClient])

	useEffect(() => {
		setOnClient(true)

		return () => {
			application?.removeAttribute('inert')
		}
	}, [])

	const dialogRole = preventClose ? ALERT : DIALOG

	if (!onClient) {
		return null
	}

	const dialogContent = (
		<dialog
			data-testid={dataTestId}
			id={id}
			className={cx(
				styles.dialog,
				className,
				nonModal ? styles.nonModal : styles.modal
			)}
			aria-label={title}
			ref={dialogRef}
			role={dialogRole}
		>
			<div
				style={location}
				data-dialog-container
				ref={animationRef}
				className={containerClassName}
			>
				{keepChildrenRendered && children}
			</div>
		</dialog>
	)

	return createPortal(dialogContent, layer ?? document.body)
}

export const Dialog = forwardRef(DialogComponent)
